import ViewLoading from "shared/UI/Spinners/ViewLoading";
import { useTranslation } from "react-i18next";
import { useCurrentUserFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { Navigate, useParams } from "react-router-dom";
import { DestinationState } from "shared/core/state/destinationState";
import "shared/extensions/string";
import { appInsightsLogger } from "core/monitoring/AppInsights";
import { useEffect } from "react";
import { AuthSsoState } from "core/state/authSsoState";
import { UseAuthResult } from "core/auth/model";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { useUserContext } from "core/state/UserContext/UserContext";
import { capitalizeString } from "core/helpers/utils";
import { AuthError } from "./AuthError";

interface AuthProps {
  sso?: boolean;
  auth: UseAuthResult;
}

export const Auth = ({ auth, sso }: AuthProps) => {
  const { provider } = useParams();
  const { t } = useTranslation();
  appInsightsLogger.info("useAuth - Auth.tsx navigating");
  const { user, saveUser, isAdditionalInfoComplete } = useUserContext();
  const { data: userResponse, isLoading, error } = useCurrentUserFunctionRequest({}, { shouldFetch: !auth.isLoading });

  useEffect(() => {
    if (userResponse) {
      saveUser(userResponse);
    }
  }, [userResponse, saveUser]);

  useEffect(() => {
    if (sso) {
      const ssoProvider = provider ? capitalizeString(provider) : AuthSsoState.get();
      const returnPath = provider ? "sso/setup" : "account/account-security/sso";
      const { REACT_APP_AUTH_AUTHORITY: authServerApi, REACT_APP_AUTH_CLIENT_ID: clientId } = process.env;
      const returnUri = encodeURIComponent(`${window.location.origin}/${returnPath}?provider=${ssoProvider}&status=success&client_id=${clientId}`);
      const uri = `${authServerApi}/sso/${ssoProvider}/setup?clientId=${clientId}&returnUrl=${returnUri}`;
      window.location.assign(uri);
    }
  }, [provider, sso]);

  if (sso) {
    return <ViewLoading label={t("auth.loading")} />;
  }

  if (ClientInfoState.getCode() === undefined) {
    appInsightsLogger.error("Client Info Error!", { clientInfo: ClientInfoState.toJson() });
    return <AuthError auth={auth} />;
  }

  if (error !== undefined) {
    appInsightsLogger.error("Get Current User Error!", error);
    return <AuthError auth={auth} />;
  }

  const isAuthOrApiCallLoading = auth.isLoading || isLoading;
  if (isAuthOrApiCallLoading || !user) {
    return <ViewLoading label={t("auth.loading")} />;
  }

  if (!isAdditionalInfoComplete()) {
    return <Navigate to="/additional-info" />;
  }

  return <Navigate to={DestinationState.get()} />;
};
